import { IValidatorInterface } from "../validator-interface";

/**
 * Networked Plus — the branded mobile app plan ladder, read from Stripe products.
 *
 * ## Why this is not part of `/plans/available`
 *
 * `availablePlans` finds products via `metadata['availablePlanv2']:'true'`, groups them by
 * `metadata.type`, and then collapses each group to at most **two** entries —
 * `{ regular, plus }` — keyed on `metadata.plus === "true"`.
 *
 * That shape cannot hold a three-tier ladder: put plus1/plus2/plus3 under one `type` and two of
 * them are silently discarded, leaving whichever was seen last.
 *
 * Worse, `fe-main`'s BillingPage reads `availablePlans[type].regular.order` without a guard, so a
 * group carrying only a `plus` entry throws `Cannot read properties of undefined` and takes the
 * billing page down — for every community, the moment the product is saved in the Stripe
 * dashboard, with no deploy involved.
 *
 * So Plus products carry their own discovery key, `metadata.networkedPlusApp = "true"`, stay
 * invisible to `availablePlans`, and are read here instead.
 *
 * ## Product setup this endpoint expects
 *
 * Product metadata (all values are strings, as Stripe requires):
 *   networkedPlusApp = "true"      discovery key
 *   planId           = plus1|plus2|plus3
 *   order            = 1|2|3       display order
 *   monthlyTokens    = raw integer, e.g. "250000"
 *   txnFeePercent    = number only, e.g. "4"
 *   isRecommended    = "true" on exactly one tier
 *   feature1..5      = the tier's selling points, one per key, in display order
 *
 * `feature1..5` are numbered keys rather than one delimited string because Stripe metadata has no
 * list type, and a comma-separated value cannot survive copy that contains a comma. Numbering
 * also fixes the display order without a second `order` field per bullet.
 *
 * Price: exactly one **active, recurring, yearly** price per product. Plus is an annual
 * commitment; a one-time price is not what is wanted and is ignored here.
 */

//Req [GET] /stripe/plans/networked-plus
export interface INetworkedPlusPlansRequest { }

//Val [GET] /stripe/plans/networked-plus — no query params.
export const VNetworkedPlusPlansGet: IValidatorInterface = {};

/**
 * The same ladder for platform staff, who have a session but no community token: the fe-admin
 * "mark paid offline" dialog picks the paid tier from it (Spencer's review Q15). Platform admins
 * only. Response: `INetworkedPlusPlansResponse`.
 */
//Req [GET] /stripe/platform/plans/networked-plus
export interface IPlatformNetworkedPlusPlansRequest { }

//Val [GET] /stripe/platform/plans/networked-plus — no query params.
export const VPlatformNetworkedPlusPlansGet: IValidatorInterface = {};

export interface INetworkedPlusPlan {
	/** Stripe product id. */
	productId: string;
	/** Stripe price id — this is what a charge is created against. */
	priceId: string;
	/** plus1 | plus2 | plus3. Stored on CommunityMobileApp.planId. */
	planId: string;
	name: string;
	/**
	 * The annual amount in MINOR units (cents) — exactly what Stripe charges.
	 *
	 * Minor units and the unit is in the field name on purpose: the tier cards display a monthly
	 * figure in whole currency units, and the two have been confused before. Monthly display is
	 * derived by the caller as `priceAnnualMinor / 12`; it is never stored, so the displayed and
	 * charged prices cannot drift apart.
	 */
	priceAnnualMinor: number;
	priceCurrency: string;
	monthlyTokens: number;
	txnFeePercent: number;
	isRecommended: boolean;
	order: number;
	/**
	 * The tier's selling points, read from `feature1..feature5` in display order.
	 *
	 * Empty when the product defines none — which is the ordinary state for a product created
	 * before these keys existed, not an error. The page keeps its built-in list in that case, so
	 * a half-configured product never renders a card with no features at all.
	 */
	features: string[];
}

//Res [GET] /stripe/plans/networked-plus
export interface INetworkedPlusPlansResponse {
	/** Ordered by `metadata.order`. Empty when no products have been created yet. */
	plans: INetworkedPlusPlan[];
}
